import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BOOSTER_COOLDOWN_MS,
  BOOSTER_COOLDOWN_START_THRESHOLD,
  BOOSTER_DAILY_MAX,
  BOOSTER_DURATION_MS,
  CONSOLATION_REWARD_POINT,
  FREE_PLAY_DAILY_BASE,
  FREE_PLAY_COOLDOWN_MS,
  MAX_CONSOLATION_REWARD_COUNT,
  PLAY_COST_POINT,
  SUCCESS_REWARD_POINT,
  TARGET_MS,
  TIME_SCORE_BOOSTER_RATE,
  TIME_SCORE_NORMAL_RATE,
  TIME_SCORE_TARGET_POINT,
  TOAST_DURATION_MS,
} from '../constants';
import { getTodayKstKey } from '../utils/time';

const BOOSTER_START_TEXT_SWITCH_MS = 820;
const BOOSTER_START_EFFECT_TOTAL_MS = 1640;
const MOCK_AD_DURATION_SEC = 3;
const AD_REST_PLAY_INTERVAL = 10;

const formatCountdownText = (ms) => {
  const safeMs = Math.max(0, ms);
  const totalSeconds = Math.ceil(safeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatSecondsLabel = (ms) => `${Math.ceil(Math.max(0, ms) / 1000)}s`;

const parseServerDateTimeToMs = (value) => {
  if (!value || typeof value !== 'string') return 0;
  const normalized = value.trim();
  if (!normalized) return 0;

  const directParsed = new Date(normalized.replace(' ', 'T')).getTime();
  if (!Number.isNaN(directParsed)) return directParsed;

  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?$/,
  );
  if (!match) return 0;
  const [, y, m, d, hh, mm, ss, msRaw = '0'] = match;
  const ms = Number(msRaw.padEnd(3, '0'));
  return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss), ms).getTime();
};

const hasServerField = (source, field) => Object.prototype.hasOwnProperty.call(source, field);

export default function useTimeNyangRewards({ currentPoint, initState = null, isMockMode = true, runRewardAd = null }) {
  const [pointBalance, setPointBalance] = useState(0);
  const [timeScore, setTimeScore] = useState(0);
  const [isExchangeReady, setIsExchangeReady] = useState(false);
  const [dailyConsolationRewardCount, setDailyConsolationRewardCount] = useState(0);
  const [freePlayDailyMax, setFreePlayDailyMax] = useState(FREE_PLAY_DAILY_BASE);
  const [freePlayRemaining, setFreePlayRemaining] = useState(FREE_PLAY_DAILY_BASE);
  const [freePlayCooldownEndAt, setFreePlayCooldownEndAt] = useState(0);
  const [isFreePlayReady, setIsFreePlayReady] = useState(false);
  const [boosterRemaining, setBoosterRemaining] = useState(BOOSTER_DAILY_MAX);
  const [boosterRemainingMs, setBoosterRemainingMs] = useState(0);
  const [boosterCooldownEndAt, setBoosterCooldownEndAt] = useState(0);
  const [boosterNeedsCooldownOnEnd, setBoosterNeedsCooldownOnEnd] = useState(false);
  const [boosterStartEffectEvent, setBoosterStartEffectEvent] = useState(null);
  const [boosterStartTextPhase, setBoosterStartTextPhase] = useState(0);
  const [nowMs, setNowMs] = useState(Date.now());
  const [rewardPopup, setRewardPopup] = useState(null);
  const [adRestPopup, setAdRestPopup] = useState(null);
  const [, setAdPlayCounter] = useState(0);
  const [mockAd, setMockAd] = useState(null);
  const [bottomToastEvents, setBottomToastEvents] = useState([]);
  const [todayKstKey, setTodayKstKey] = useState(getTodayKstKey);
  const bottomToastTimersRef = useRef(new Map());
  const mockAdCompleteRef = useRef(null);

  const emitBottomToast = useCallback((text, type = 'default') => {
    const toastId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setBottomToastEvents((prev) => [...prev, { id: toastId, text, type }]);

    const timer = window.setTimeout(() => {
      setBottomToastEvents((prev) => prev.filter((event) => event.id !== toastId));
      bottomToastTimersRef.current.delete(toastId);
    }, TOAST_DURATION_MS);
    bottomToastTimersRef.current.set(toastId, timer);
  }, []);

  const freePlayCooldownMsLeft = Math.max(0, freePlayCooldownEndAt - nowMs);
  const boosterCooldownMsLeft = Math.max(0, boosterCooldownEndAt - nowMs);
  const isBoosterActive = boosterRemainingMs > 0;
  const isBoosterOnCooldown = boosterCooldownMsLeft > 0;
  const isBoosterPausedByRewardPopup =
    isBoosterActive &&
    (rewardPopup?.kind === 'success' || rewardPopup?.kind === 'near-point' || rewardPopup?.kind === 'near-free');

  const runMockAd = useCallback((kind, onComplete) => {
    mockAdCompleteRef.current = onComplete;
    setMockAd({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind,
      secondsLeft: MOCK_AD_DURATION_SEC,
    });
  }, []);

  const runRewardAdOrMock = useCallback(
    (kind, onComplete) => {
      if (!isMockMode && typeof runRewardAd === 'function') {
        runRewardAd(kind, onComplete);
        return;
      }
      runMockAd(kind, onComplete);
    },
    [isMockMode, runMockAd, runRewardAd],
  );

  const applyServerState = useCallback((serverState) => {
    if (!serverState) return;

    const now = Date.now();

    if (hasServerField(serverState, 'timeScore')) {
      setTimeScore(Number(serverState.timeScore ?? 0));
    }
    if (hasServerField(serverState, 'exchangeReady')) {
      setIsExchangeReady(Boolean(serverState.exchangeReady));
    }
    if (hasServerField(serverState, 'comfortDailyCount')) {
      setDailyConsolationRewardCount(Number(serverState.comfortDailyCount ?? 0));
    }

    if (hasServerField(serverState, 'freeBonusLeft')) {
      setFreePlayDailyMax(FREE_PLAY_DAILY_BASE + Number(serverState.freeBonusLeft ?? 0));
    }
    if (hasServerField(serverState, 'freeDailyLeft') || hasServerField(serverState, 'freeBonusLeft')) {
      setFreePlayRemaining((prev) => {
        const freeDailyLeft = hasServerField(serverState, 'freeDailyLeft')
          ? Number(serverState.freeDailyLeft ?? 0)
          : Math.max(0, prev - Number(serverState.freeBonusLeft ?? 0));
        const freeBonusLeft = hasServerField(serverState, 'freeBonusLeft')
          ? Number(serverState.freeBonusLeft ?? 0)
          : Math.max(0, prev - FREE_PLAY_DAILY_BASE);
        return Math.max(0, freeDailyLeft + freeBonusLeft);
      });
    }
    if (hasServerField(serverState, 'freeCooldownEnd')) {
      setFreePlayCooldownEndAt(parseServerDateTimeToMs(serverState.freeCooldownEnd));
    }
    if (hasServerField(serverState, 'freePlayActive')) {
      setIsFreePlayReady(Number(serverState.freePlayActive ?? 0) === 1);
    }

    if (hasServerField(serverState, 'boosterDailyUsed')) {
      const boosterDailyUsed = Number(serverState.boosterDailyUsed ?? 0);
      setBoosterRemaining(Math.max(0, BOOSTER_DAILY_MAX - boosterDailyUsed));
    }
    if (hasServerField(serverState, 'boosterActive') || hasServerField(serverState, 'boosterEndAt')) {
      const boosterEndAtMs = parseServerDateTimeToMs(serverState.boosterEndAt);
      const isActive = hasServerField(serverState, 'boosterActive')
        ? Number(serverState.boosterActive ?? 0) === 1
        : boosterEndAtMs > now;

      if (!isActive) {
        setBoosterRemainingMs(0);
      } else if (boosterEndAtMs > 0) {
        setBoosterRemainingMs(Math.max(0, boosterEndAtMs - now));
      }
      setBoosterNeedsCooldownOnEnd(false);
    }
    if (hasServerField(serverState, 'boosterCooldownEnd')) {
      setBoosterCooldownEndAt(parseServerDateTimeToMs(serverState.boosterCooldownEnd));
      setBoosterNeedsCooldownOnEnd(false);
    }
  }, []);

  const getStartContext = useCallback(() => {
    if (isFreePlayReady) {
      return { canStart: true, mode: 'free' };
    }

    if (pointBalance >= PLAY_COST_POINT) {
      return { canStart: true, mode: 'paid' };
    }
    return { canStart: false, reason: 'insufficient-point' };
  }, [freePlayRemaining, isFreePlayReady, pointBalance]);

  const handlePlayStart = useCallback(
    (mode) => {
      const scoreRate = isBoosterActive ? TIME_SCORE_BOOSTER_RATE : TIME_SCORE_NORMAL_RATE;
      setTimeScore((prev) => Number((prev + PLAY_COST_POINT * scoreRate).toFixed(1)));

      if (mode === 'free') {
        setIsFreePlayReady(false);
        return;
      }
      setPointBalance((prev) => Math.max(0, prev - PLAY_COST_POINT));
    },
    [isBoosterActive],
  );

  const handlePlayStartResponse = useCallback((payload, mode) => {
    if (Number.isFinite(Number(payload?.balance))) {
      setPointBalance(Number(payload.balance));
    }
    if (mode === 'free') {
      setIsFreePlayReady(false);
    }
  }, []);

  const handlePlayStopResponse = useCallback(
    (payload) => {
      if (Number.isFinite(Number(payload?.balance))) {
        setPointBalance(Number(payload.balance));
      }
      applyServerState(payload?.state);
    },
    [applyServerState],
  );

  const handleFreePlayChipClick = useCallback(() => {
    if (freePlayRemaining <= 0) {
      return;
    }
    if (freePlayCooldownMsLeft > 0) {
      return;
    }
    if (isFreePlayReady) return;

    const prepareFreePlay = () => {
      if (isMockMode) {
        setFreePlayRemaining((prev) => Math.max(0, prev - 1));
        setIsFreePlayReady(true);
        setFreePlayCooldownEndAt(Date.now() + FREE_PLAY_COOLDOWN_MS);
      }
    };

    if (!isMockMode) {
      runRewardAdOrMock('free-play', () => {
        setFreePlayRemaining((prev) => Math.max(0, prev - 1));
        setIsFreePlayReady(true);
        setFreePlayCooldownEndAt(Date.now() + FREE_PLAY_COOLDOWN_MS);
      });
      return;
    }

    prepareFreePlay();
  }, [freePlayCooldownMsLeft, freePlayRemaining, isFreePlayReady, isMockMode, runRewardAdOrMock]);

  const startBooster = useCallback((useIndex) => {
    setBoosterRemaining((prev) => Math.max(0, prev - 1));
    setBoosterRemainingMs(BOOSTER_DURATION_MS);
    setBoosterNeedsCooldownOnEnd(useIndex >= BOOSTER_COOLDOWN_START_THRESHOLD);
    setBoosterStartEffectEvent({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    });
    setBoosterStartTextPhase(0);
  }, []);

  const handleBoosterChipClick = useCallback(() => {
    if (boosterRemaining <= 0) return;
    if (isBoosterActive || isBoosterOnCooldown || mockAd) return;

    const useIndex = BOOSTER_DAILY_MAX - boosterRemaining + 1;
    runRewardAdOrMock('booster-start', () => startBooster(useIndex));
  }, [boosterRemaining, isBoosterActive, isBoosterOnCooldown, mockAd, runRewardAdOrMock, startBooster]);

  const handleRoundResult = useCallback(
    ({ type, clampedMs, successCount }) => {
      if (!isBoosterActive) {
        setAdPlayCounter((prev) => {
          const next = prev + 1;
          if (next < AD_REST_PLAY_INTERVAL) return next;
          if (type !== 'success' && type !== 'near') {
            setAdRestPopup({ kind: 'ad-rest' });
          }
          return 0;
        });
      }

      if (type === 'success') {
        setRewardPopup({ kind: 'success', successCount });
        return;
      }
      if (type === 'timeout') {
        const overDiffSec = ((TARGET_MS - clampedMs) / 1000).toFixed(2);
        emitBottomToast(`😾 타임오버! (${overDiffSec}초)`, 'timeout');
        return;
      }
      if (type === 'near') {
        const nextTurn = dailyConsolationRewardCount + 1;
        if (dailyConsolationRewardCount >= MAX_CONSOLATION_REWARD_COUNT) {
          setRewardPopup({ kind: 'near-exhausted' });
        } else if (nextTurn === 4) {
          setRewardPopup({ kind: 'near-free' });
        } else {
          setRewardPopup({ kind: 'near-point', turn: nextTurn });
        }
      }
    },
    [dailyConsolationRewardCount, emitBottomToast, isBoosterActive],
  );

  const closeRewardPopup = useCallback(() => {
    setRewardPopup(null);
  }, []);

  const handleRewardConfirm = useCallback(() => {
    if (!rewardPopup) return;
    if (mockAd) return;

    const confirmReward = () => {
      if (isMockMode) {
        if (rewardPopup.kind === 'success') {
          setPointBalance((prev) => prev + SUCCESS_REWARD_POINT);
          emitBottomToast('🎉 성공 보상 400P 지급!', 'success');
        } else if (rewardPopup.kind === 'near-point') {
          setPointBalance((prev) => prev + CONSOLATION_REWARD_POINT);
          setDailyConsolationRewardCount((prev) => Math.min(MAX_CONSOLATION_REWARD_COUNT, prev + 1));
          emitBottomToast('💰 0.01 혜택 10P 지급!', 'near');
        } else if (rewardPopup.kind === 'near-free') {
          setDailyConsolationRewardCount((prev) => Math.min(MAX_CONSOLATION_REWARD_COUNT, prev + 1));
          setFreePlayDailyMax((prev) => prev + 1);
          setFreePlayRemaining((prev) => prev + 1);
          emitBottomToast('🎮 무료탐냥 횟수 +1 추가!', 'default');
        }
      }
      setRewardPopup(null);
    };

    if (rewardPopup.kind === 'success') {
      runRewardAdOrMock('success-reward', confirmReward);
      return;
    } else if (rewardPopup.kind === 'near-point') {
      runRewardAdOrMock('near-point-reward', confirmReward);
      return;
    } else if (rewardPopup.kind === 'near-free') {
      confirmReward();
      return;
    }
    confirmReward();
  }, [emitBottomToast, isMockMode, mockAd, rewardPopup, runRewardAdOrMock]);

  const handleRewardSkip = useCallback(() => {
    setRewardPopup(null);
  }, []);

  const handleAdRestConfirm = useCallback(() => {
    if (!adRestPopup || mockAd) return;
    runRewardAdOrMock('ad-rest', () => {
      if (isMockMode) {
        setPointBalance((prev) => prev + 1);
        emitBottomToast('📺 광고 보상 1P 지급!', 'default');
      }
      setAdRestPopup(null);
    });
  }, [adRestPopup, emitBottomToast, isMockMode, mockAd, runRewardAdOrMock]);

  const handleAdRestSkip = useCallback(() => {
    setAdRestPopup(null);
  }, []);

  const handleNPayUnavailableClick = useCallback(() => {
    emitBottomToast('3,000P 달성 시 교환 가능', 'default');
  }, [emitBottomToast]);

  const handleNPayExchangeConfirm = useCallback((payload = null) => {
    if (Number.isFinite(Number(payload?.timeScore))) {
      setTimeScore(Number(payload.timeScore));
    } else {
      setTimeScore((prev) => Number(Math.max(0, prev - TIME_SCORE_TARGET_POINT).toFixed(1)));
    }
    setIsExchangeReady(false);
    emitBottomToast('N Pay 2천원권 수령 완료!', 'success');
  }, [emitBottomToast]);

  useEffect(() => {
    setPointBalance(currentPoint);
  }, [currentPoint, isMockMode]);

  useEffect(() => {
    if (isMockMode) return;
    const serverState = initState?.state;
    if (!serverState) return;
    applyServerState(serverState);
  }, [applyServerState, initState, isMockMode]);

  useEffect(() => {
    if (isMockMode) return;
    if (typeof initState?.exchangeReady === 'boolean') {
      setIsExchangeReady(initState.exchangeReady);
    }
  }, [initState?.exchangeReady, isMockMode]);

  useEffect(() => {
    if (!isMockMode) return;
    setDailyConsolationRewardCount(0);
    setFreePlayDailyMax(FREE_PLAY_DAILY_BASE);
    setFreePlayRemaining(FREE_PLAY_DAILY_BASE);
    setFreePlayCooldownEndAt(0);
    setIsFreePlayReady(false);
    setBoosterRemaining(BOOSTER_DAILY_MAX);
    setBoosterRemainingMs(0);
    setBoosterCooldownEndAt(0);
    setBoosterNeedsCooldownOnEnd(false);
  }, [isMockMode, todayKstKey]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
      if (isBoosterPausedByRewardPopup) return;
      setBoosterRemainingMs((prev) => {
        if (prev <= 0) return 0;
        const next = Math.max(0, prev - 250);
        if (next === 0 && boosterNeedsCooldownOnEnd) {
          setBoosterCooldownEndAt(Date.now() + BOOSTER_COOLDOWN_MS);
          setBoosterNeedsCooldownOnEnd(false);
        }
        return next;
      });
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [boosterNeedsCooldownOnEnd, isBoosterPausedByRewardPopup]);

  useEffect(() => {
    if (freePlayCooldownEndAt && freePlayCooldownEndAt <= nowMs) {
      setFreePlayCooldownEndAt(0);
    }
  }, [freePlayCooldownEndAt, nowMs]);

  useEffect(() => {
    if (boosterCooldownEndAt && boosterCooldownEndAt <= nowMs) {
      setBoosterCooldownEndAt(0);
    }
  }, [boosterCooldownEndAt, nowMs]);

  useEffect(() => {
    if (!mockAd) return undefined;

    const intervalId = window.setInterval(() => {
      setMockAd((prev) => {
        if (!prev) return null;
        const nextSecondsLeft = prev.secondsLeft - 1;
        if (nextSecondsLeft > 0) return { ...prev, secondsLeft: nextSecondsLeft };

        window.clearInterval(intervalId);
        const onComplete = mockAdCompleteRef.current;
        mockAdCompleteRef.current = null;
        window.setTimeout(() => {
          onComplete?.();
        }, 0);
        return null;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [mockAd]);

  useEffect(() => {
    if (!boosterStartEffectEvent?.id) return undefined;
    setBoosterStartTextPhase(0);
    const switchTimerId = window.setTimeout(() => {
      setBoosterStartTextPhase(1);
    }, BOOSTER_START_TEXT_SWITCH_MS);
    const closeTimerId = window.setTimeout(() => {
      setBoosterStartEffectEvent(null);
    }, BOOSTER_START_EFFECT_TOTAL_MS);

    return () => {
      window.clearTimeout(switchTimerId);
      window.clearTimeout(closeTimerId);
    };
  }, [boosterStartEffectEvent?.id]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const nextTodayKstKey = getTodayKstKey();
      if (nextTodayKstKey !== todayKstKey) {
        setTodayKstKey(nextTodayKstKey);
      }
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [todayKstKey]);

  useEffect(
    () => () => {
      bottomToastTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      bottomToastTimersRef.current.clear();
    },
    [],
  );

  const startContext = getStartContext();
  const idleStartButtonLabel =
    !startContext.canStart && startContext.reason === 'free-play-cooldown'
      ? `무료 ${formatCountdownText(startContext.cooldownMsLeft)}`
      : !startContext.canStart && startContext.reason === 'no-free-play'
        ? '무료 소진'
        : !startContext.canStart && startContext.reason === 'insufficient-point'
          ? '8P 필요'
          : isFreePlayReady
          ? '무료 START'
          : isBoosterActive
            ? '부스트 START'
            : 'START';
  const freePlayCooldownLabel = freePlayCooldownMsLeft > 0 ? formatSecondsLabel(freePlayCooldownMsLeft) : '';
  const isFreePlayOnCooldown = freePlayCooldownMsLeft > 0;
  const isFreePlayDisabled = freePlayRemaining <= 0 || isFreePlayOnCooldown || isFreePlayReady;
  const boosterStatusLabel = isBoosterActive
    ? formatSecondsLabel(boosterRemainingMs)
    : boosterCooldownMsLeft > 0
      ? formatSecondsLabel(boosterCooldownMsLeft)
      : '';

  return {
    pointBalance,
    timeScore,
    canExchangeNPay: isMockMode ? timeScore >= TIME_SCORE_TARGET_POINT : isExchangeReady || timeScore >= TIME_SCORE_TARGET_POINT,
    freePlayDailyMax,
    freePlayRemaining,
    freePlayCooldownMsLeft,
    freePlayCooldownLabel,
    isFreePlayOnCooldown,
    isFreePlayDisabled,
    boosterRemaining,
    boosterStatusLabel,
    isBoosterOnCooldown,
    isBoosterActive,
    isBoosterPausedByRewardPopup,
    boosterStartEffectEvent,
    boosterStartTextPhase,
    rewardPopup,
    adRestPopup,
    mockAd,
    bottomToastEvents,
    getStartContext,
    handlePlayStart,
    handlePlayStartResponse,
    handlePlayStopResponse,
    handleRoundResult,
    closeRewardPopup,
    handleRewardConfirm,
    handleRewardSkip,
    handleAdRestConfirm,
    handleAdRestSkip,
    handleNPayUnavailableClick,
    handleNPayExchangeConfirm,
    handleFreePlayChipClick,
    handleBoosterChipClick,
    idleStartButtonLabel,
    isIdleStartDisabled: !startContext.canStart,
    isFreePlayReady,
    isBlockedByPopup: Boolean(rewardPopup || adRestPopup || mockAd),
    showBottomToast: emitBottomToast,
  };
}
